// Brand pivot (FLOSEAI-200): teal-green-gold "Refined Dark Luxury".
// Spec anchors (preserve exactly): bg primary #0A1A2A, card #0F2D2A,
// primary accent #00C4B4, secondary accent #1A7A6A, warning #B8860B,
// text secondary #8BA8B8. Stored as HSL for consistency with the rest
// of the system; original hex values are noted in comments.
export const colors = {
  background: {
    primary: 'hsl(212, 62%, 11%)', // #0A1A2A (spec)
    secondary: 'hsl(208, 60%, 13%)', // #0D2233 (spec)
    // There is deliberately no separate "shell" tone. Sidebar, header and
    // content all sit on `primary`, so the app shell is one uninterrupted
    // surface and the only thing separating chrome from content is spacing.
    // A darker frame tone was tried and removed: it read as a container drawn
    // around the content rather than as the edge of the app.
    tertiary: 'hsl(190, 50%, 13%)', // intermediate teal-navy
    elevated: 'hsl(175, 35%, 17%)', // elevated surface toward teal
    overlay: 'hsla(212, 62%, 11%, 0.84)',
    scrim: 'rgba(0, 0, 0, 0.6)',
    card: 'hsla(173, 51%, 12%, 0.86)', // #0F2D2A (spec) with translucency
    input: 'hsl(195, 45%, 13%)',
    backdropTinted: 'hsla(205, 44%, 7%, 0.68)', // drawer/mobile backdrop (used 2x)
    gradient: {
      start: 'hsl(212, 62%, 11%)',
      middle: 'hsl(195, 50%, 13%)',
      end: 'hsl(175, 45%, 17%)',
    },
  },

  foreground: 'hsl(0, 0%, 100%)',

  primary: {
    50: 'hsl(176, 80%, 95%)',
    100: 'hsl(175, 75%, 88%)',
    200: 'hsl(175, 70%, 78%)',
    300: 'hsl(175, 80%, 65%)',
    400: 'hsl(175, 90%, 50%)',
    500: 'hsl(175, 100%, 38%)', // #00C4B4 (spec)
    600: 'hsl(175, 100%, 32%)',
    700: 'hsl(175, 95%, 26%)',
    800: 'hsl(175, 90%, 20%)',
    900: 'hsl(170, 85%, 15%)',
    main: 'hsl(175, 100%, 38%)', // #00C4B4 (spec)
    neon: 'hsl(173, 100%, 50%)', // brighter pulse variant for glow
    cyan: 'hsl(175, 100%, 38%)', // back-compat alias → same as main
    faint: 'hsla(175, 100%, 38%, 0.08)',
    muted: 'hsla(175, 100%, 38%, 0.14)',
    subtle: 'hsla(175, 100%, 38%, 0.22)',
    borderActive: 'hsla(175, 100%, 38%, 0.4)',
    border: 'hsla(175, 100%, 38%, 0.5)',
    gradient: ['hsl(175, 100%, 38%)', 'hsl(170, 65%, 29%)'] as readonly [string, string],
  },

  'primary-foreground': 'hsl(212, 62%, 8%)',

  secondary: 'hsl(170, 65%, 29%)', // #1A7A6A (spec)

  muted: 'hsl(190, 40%, 14%)',
  'muted-foreground': 'hsl(200, 24%, 64%)',

  accent: {
    cyan: {
      neon: 'hsl(173, 100%, 55%)',
      glow: 'hsla(173, 100%, 55%, 0.42)',
    },
    purple: {
      50: 'hsl(253, 100%, 95%)',
      100: 'hsl(252, 100%, 90%)',
      200: 'hsl(251, 95%, 82%)',
      300: 'hsl(249, 92%, 74%)',
      400: 'hsl(246, 92%, 66%)',
      500: 'hsl(243, 90%, 58%)',
      600: 'hsl(241, 82%, 49%)',
      700: 'hsl(240, 74%, 40%)',
      800: 'hsl(239, 66%, 32%)',
      900: 'hsl(239, 58%, 25%)',
      neon: 'hsl(244, 95%, 62%)',
      bg: 'hsla(243, 90%, 58%, 0.14)', // badge / chip tint
      gradientStart: 'hsla(265, 80%, 65%, 0.2)',
      gradientEnd: 'hsla(265, 80%, 65%, 0.1)',
    },
    blue: {
      50: 'hsl(209, 100%, 95%)',
      100: 'hsl(209, 100%, 89%)',
      200: 'hsl(207, 98%, 81%)',
      300: 'hsl(207, 96%, 72%)',
      400: 'hsl(206, 96%, 64%)',
      500: 'hsl(206, 96%, 55%)',
      600: 'hsl(206, 90%, 47%)',
      700: 'hsl(206, 82%, 39%)',
      800: 'hsl(206, 74%, 32%)',
      900: 'hsl(206, 66%, 26%)',
      neon: 'hsl(206, 100%, 62%)',
    },
    amber: {
      50: 'hsl(45, 100%, 95%)',
      100: 'hsl(44, 100%, 89%)',
      200: 'hsl(43, 98%, 79%)',
      300: 'hsl(43, 95%, 65%)',
      400: 'hsl(43, 92%, 50%)',
      500: 'hsl(43, 89%, 38%)', // aligned with new warning (#B8860B)
      600: 'hsl(42, 88%, 32%)',
      700: 'hsl(40, 85%, 26%)',
      800: 'hsl(38, 80%, 20%)',
      900: 'hsl(35, 75%, 16%)',
      neon: 'hsl(43, 95%, 52%)',
    },
    gradient: {
      cyanToPurple: ['hsl(175, 100%, 38%)', 'hsl(244, 95%, 62%)'] as readonly [string, string],
      cyanToBlue: ['hsl(175, 100%, 38%)', 'hsl(206, 100%, 62%)'] as readonly [string, string],
      cyanToAmber: ['hsl(175, 100%, 38%)', 'hsl(43, 95%, 52%)'] as readonly [string, string],
    },
  },

  // The eight container roles of the design system. Every background in the
  // app resolves to one of these: the two canvas fills above
  // (background.primary / background.secondary) plus the six below. Anything
  // that does not fit a role belongs in a role, not in a new hsla().
  surface: {
    // Level 1 — the default card fill, in two renderings of one role.
    // Web gets "ultra-clear" glass: a near-neutral white veil over a backdrop
    // blur, so a page of cards reads as one continuous teal field instead of a
    // stack of separately tinted panes. Native has no backdrop-filter, so it
    // falls back to the 160deg teal-ink gradient at the same perceived depth,
    // painted by BentoSurface with expo-linear-gradient.
    bento: {
      fill: 'hsla(0, 0%, 100%, 0.03)',
      fillHover: 'hsla(0, 0%, 100%, 0.05)',
      border: 'hsla(0, 0%, 100%, 0.08)',
      sheen: 'hsla(0, 0%, 100%, 0.06)',
      blur: 10,
      gradient: ['hsla(175, 40%, 14%, 0.5)', 'hsla(208, 42%, 10%, 0.5)'] as readonly [
        string,
        string,
      ],
      gradientSheen: ['hsla(175, 100%, 50%, 0.1)', 'hsla(175, 100%, 50%, 0)'] as readonly [
        string,
        string,
      ],
    },
    // Level 1 — dense panels, tables and modals, where the bento sheen would
    // fight the content grid.
    cardOverlay: 'hsla(173, 35%, 14%, 0.82)',
    // Level 2 — grouping inside a card. Never bordered, never shadowed.
    row: 'hsla(0, 0%, 100%, 0.04)',
    rowHover: 'hsla(0, 0%, 100%, 0.07)',
    // Chrome — always blurred, never opaque.
    navChrome: 'hsla(208, 30%, 17%, 0.72)',
    sidebar: 'hsla(208, 38%, 12%, 0.94)',
    // Level 2 — the well behind a BrandIcon glyph.
    iconWell: 'hsla(173, 30%, 14%, 0.74)',
  },

  interactive: {
    hover: 'hsla(175, 100%, 38%, 0.16)',
    focus: 'hsla(175, 100%, 38%, 0.26)',
    active: 'hsla(175, 100%, 38%, 0.3)',
    hoverSubtle: 'hsla(0, 0%, 100%, 0.06)',
  },

  text: {
    primary: 'hsl(0, 0%, 100%)', // #FFFFFF (spec)
    secondary: 'hsl(200, 24%, 64%)', // #8BA8B8 (spec)
    // Uppercase eyebrows and field labels. Lifted from 52% so small text clears
    // AA (4.5:1) on every canonical surface, including background.elevated
    // where the old value measured 3.42:1.
    tertiary: 'hsl(200, 20%, 62%)',
    disabled: 'hsl(200, 12%, 45%)',
    inverse: 'hsl(212, 62%, 8%)',
    inverseSubtle: 'hsla(212, 62%, 8%, 0.8)',
    muted: 'hsl(200, 18%, 56%)',
    body: 'hsl(200, 24%, 64%)',
    description: 'hsl(200, 22%, 70%)',
    stats: 'hsl(0, 0%, 96%)',
    interactive: 'hsl(173, 100%, 50%)',
    onAccent: 'hsla(0, 0%, 100%, 0.7)', // text on teal/brand surfaces
  },

  shimmer: {
    base: 'rgba(255, 255, 255, 0.08)',
    highlight: 'rgba(255, 255, 255, 0.1)',
  },

  border: {
    default: 'hsl(180, 25%, 28%)',
    subtle: 'hsla(180, 28%, 26%, 0.45)',
    strong: 'hsl(175, 30%, 36%)',
    focus: 'hsl(175, 100%, 38%)',
    muted: 'hsla(180, 22%, 22%, 0.74)',
    input: 'hsl(190, 25%, 24%)',
    glassCard: 'hsla(215, 28%, 30%, 0.3)', // glass-morphism card border
  },

  brand: {
    cyan: 'hsl(175, 100%, 38%)', // back-compat alias → new primary
    'cyan-dark': 'hsl(175, 95%, 26%)',
    amber: 'hsl(43, 89%, 38%)', // aligned with new warning
    // Social brand colors stay as hex per official brand guidelines
    instagram: '#E1306C',
    instagramBg: 'rgba(225, 48, 108, 0.08)',
    facebook: '#1877F2',
    facebookBg: 'rgba(24, 119, 242, 0.08)',
    twitter: '#1DA1F2',
    twitterBg: 'rgba(29, 161, 242, 0.08)',
    linkedin: '#0A66C2',
    linkedinBg: 'rgba(10, 102, 194, 0.08)',
    linkedinTint: 'hsla(198, 80%, 50%, 0.18)',
    linkedinTintBorder: 'hsla(198, 80%, 50%, 0.3)',
  },

  // Agent-research palette — distinct from primary teal. Phase 5 will
  // re-verify these still feel intentional against the new palette.
  agentSource: {
    linkedin: {
      solid: 'hsl(206, 100%, 50%)',
      glow: 'hsla(206, 100%, 50%, 0.4)',
      muted: 'hsla(206, 100%, 50%, 0.14)',
    },
    browser: {
      solid: 'hsl(38, 100%, 60%)',
      glow: 'hsla(38, 100%, 60%, 0.4)',
      muted: 'hsla(38, 100%, 60%, 0.14)',
    },
    instagram: {
      solid: 'hsl(330, 80%, 55%)',
      glow: 'hsla(330, 80%, 55%, 0.4)',
      muted: 'hsla(330, 80%, 55%, 0.14)',
    },
    internal: {
      solid: 'hsl(173, 100%, 50%)', // re-anchored to new primary neon
      glow: 'hsla(173, 100%, 50%, 0.42)',
      muted: 'hsla(175, 100%, 38%, 0.14)',
    },
  },

  // Status hues NOT in spec — derived to harmonize with teal-green card surface.
  // Success: fresh emerald (sibling hue to primary teal, distinguishable).
  // Warning: spec's amber gold #B8860B.
  // Error: warm red, must read on dark teal-green bg.
  // Info: cooler blue, distinct from primary teal.
  status: {
    success: {
      light: 'hsl(150, 75%, 60%)',
      main: 'hsl(150, 70%, 42%)',
      dark: 'hsl(150, 65%, 32%)',
      bg: 'hsla(150, 70%, 42%, 0.14)',
      gradientStart: 'hsla(150, 70%, 42%, 0.15)',
      gradientEnd: 'hsla(150, 70%, 42%, 0.08)',
    },
    warning: {
      light: 'hsl(43, 95%, 52%)',
      main: 'hsl(43, 89%, 38%)', // #B8860B (spec)
      dark: 'hsl(40, 85%, 28%)',
      bg: 'hsla(43, 89%, 38%, 0.16)',
      border: 'hsla(43, 89%, 38%, 0.55)',
    },
    error: {
      light: 'hsl(355, 80%, 70%)',
      // Lightness raised from 58% to 65%. At 58% this measured 3.73:1 against
      // `background.card`, under the 4.5:1 AA minimum for text — and it is
      // read as text: the slow band in `TimeInStageChart` renders it as the
      // stage's duration label. It was the only one of the four status tokens
      // below AA on that surface, so this aligns it with its siblings rather
      // than inventing a threshold for one colour.
      //
      // Hue and saturation are deliberately untouched. On a dark surface the
      // only axis that buys contrast is lightness, and 65% is the smallest
      // whole step that clears the minimum: 64% still measures 4.44:1.
      main: 'hsl(355, 75%, 65%)',
      dark: 'hsl(355, 70%, 45%)',
      // `bg` and `border` stay anchored at 58% on purpose. They are fills, not
      // text, so no contrast minimum applies to them, and a 14% tint of either
      // lightness is indistinguishable — re-alpha'ing them would repaint
      // surfaces across the app for no accessibility gain.
      bg: 'hsla(355, 75%, 58%, 0.14)',
      border: 'hsla(355, 75%, 58%, 0.55)',
    },
    info: {
      light: 'hsl(210, 90%, 72%)',
      main: 'hsl(210, 90%, 60%)',
      dark: 'hsl(210, 80%, 45%)',
      bg: 'hsla(210, 90%, 60%, 0.14)',
      gradientStart: 'hsla(210, 90%, 60%, 0.15)',
      gradientEnd: 'hsla(210, 90%, 60%, 0.08)',
    },
  },

  neutral: {
    50: 'hsl(180, 30%, 96%)',
    100: 'hsl(195, 28%, 92%)',
    200: 'hsl(195, 22%, 82%)',
    300: 'hsl(195, 18%, 70%)',
    400: 'hsl(200, 16%, 58%)',
    500: 'hsl(200, 14%, 55%)',
    600: 'hsl(200, 18%, 35%)',
    700: 'hsl(195, 24%, 26%)',
    800: 'hsl(190, 28%, 16%)',
    900: 'hsl(212, 62%, 11%)',
  },

  transparent: 'transparent',
  white: '#ffffff',
  black: '#000000',

  metrics: {
    revenue: 'hsl(150, 70%, 42%)', // emerald — money green
    visitors: 'hsl(175, 100%, 50%)', // primary teal neon
    conversion: 'hsl(265, 80%, 65%)', // distinct purple identity
    campaigns: 'hsl(38, 95%, 56%)', // distinct amber from warning
    visitorsSoft: 'hsla(175, 100%, 50%, 0.18)',
    conversionSoft: 'hsla(265, 80%, 65%, 0.18)',
    gradients: {
      revenue: ['hsla(150, 70%, 42%, 0.24)', 'hsla(150, 70%, 42%, 0.04)'] as readonly [
        string,
        string,
      ],
      visitors: ['hsla(175, 100%, 50%, 0.24)', 'hsla(175, 100%, 50%, 0.04)'] as readonly [
        string,
        string,
      ],
      conversion: ['hsla(265, 80%, 65%, 0.24)', 'hsla(265, 80%, 65%, 0.04)'] as readonly [
        string,
        string,
      ],
      campaigns: ['hsla(38, 95%, 56%, 0.24)', 'hsla(38, 95%, 56%, 0.04)'] as readonly [
        string,
        string,
      ],
      default: ['hsla(175, 100%, 50%, 0.24)', 'hsla(210, 90%, 60%, 0.04)'] as readonly [
        string,
        string,
      ],
    },
  },

  gradients: {
    heroAccent: [
      'hsla(175, 100%, 50%, 0.2)',
      'hsla(160, 70%, 50%, 0.08)',
      'hsla(43, 89%, 50%, 0.08)',
    ] as readonly [string, string, string],
    // Teal bloom behind an active or focused element: the sidebar's current
    // nav item, a focused search field, the dashboard hero. Replaces three
    // hand-written cyan/blue washes that predated the teal brand.
    activeWash: ['hsla(175, 100%, 50%, 0.18)', 'hsla(175, 100%, 50%, 0)'] as readonly [
      string,
      string,
    ],
    // The brand wordmark sweep, used on the landing hero and the waitlist.
    brandText:
      'linear-gradient(110deg, hsl(175, 70%, 78%), hsl(173, 100%, 50%) 55%, hsl(206, 96%, 64%))',
  },

  glow: {
    cyan: 'hsla(175, 100%, 50%, 0.32)',
    purple: 'hsla(265, 80%, 65%, 0.3)',
    green: 'hsla(150, 70%, 42%, 0.28)',
    amber: 'hsla(43, 89%, 50%, 0.3)',
    error: 'hsla(355, 75%, 58%, 0.28)',
    info: 'hsla(210, 90%, 60%, 0.25)',
  },

  platform: {
    ios: 'hsl(206, 100%, 62%)',
    android: 'hsl(150, 70%, 42%)',
    web: 'hsl(38, 100%, 60%)',
  },
} as const;

export type ColorScheme = typeof colors;
export type ColorCategory = keyof ColorScheme;

export const getColorValue = (path: string): string => {
  const keys = path.split('.');
  let current: unknown = colors;

  for (const key of keys) {
    if (typeof current !== 'object' || current === null || !(key in current)) {
      console.warn(`Color path "${path}" not found`);
      return colors.text.primary;
    }

    current = (current as Record<string, unknown>)[key];
  }

  if (typeof current !== 'string') {
    console.warn(
      `Color path "${path}" resolved to a non-string value (${
        Array.isArray(current) ? 'array' : typeof current
      }); falling back to text.primary. Use direct dot access for gradient tuples or nested groups.`,
    );
    return colors.text.primary;
  }

  return current;
};
